import {
  fetchAllApplications,
  resolveAssignedApplicationsForSession,
} from "../../utils/matchesAssignedApplicationOrigin";
import { getToken } from "../../services/storageService";
import { readCachedSessionUser } from "./authSession.storage";
import type { AuthSessionUser } from "./authSession.types";

export const SSO_PREFETCH_PARAM = "variphi_sso_prefetch";
const SSO_PREFETCH_MESSAGE = "variphi_sso_prefetch_done";
const SSO_PREFETCH_DELAY_MS = 2500;

/** Opt-in only — sister-app iframes load surveillance/inbound/bizeye and fire /me in DevTools. */
function isSisterAppPrefetchEnabled(): boolean {
  const raw = (
    process.env.REACT_APP_SSO_PREFETCH_SISTER_APPS ?? ""
  )
    .toString()
    .trim()
    .toLowerCase();
  return raw === "1" || raw === "true" || raw === "yes";
}

export function isSsoPrefetchRequest(): boolean {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get(SSO_PREFETCH_PARAM) === "1";
}

export function notifySsoPrefetchComplete(): void {
  if (typeof window === "undefined" || window === window.top) return;
  window.parent.postMessage(
    { type: SSO_PREFETCH_MESSAGE, origin: window.location.origin },
    "*",
  );
}

function sameRegistrableDomain(originA: string, originB: string): boolean {
  try {
    const a = new URL(originA).hostname.split(".").slice(-2).join(".");
    const b = new URL(originB).hostname.split(".").slice(-2).join(".");
    return Boolean(a) && a === b;
  } catch {
    return false;
  }
}

/**
 * Optionally warm sister-app sessions in hidden iframes.
 * Disabled by default — set REACT_APP_SSO_PREFETCH_SISTER_APPS=true to enable.
 * Never prefetches cross-domain apps (e.g. bizeye.senrysa.in from variphi.com).
 */
export async function prefetchSisterAppSessions(user: AuthSessionUser): Promise<void> {
  if (typeof window === "undefined" || isSsoPrefetchRequest()) return;
  if (!isSisterAppPrefetchEnabled()) return;
  if (!getToken() || !readCachedSessionUser()) return;

  await new Promise<void>((resolve) => {
    window.setTimeout(resolve, SSO_PREFETCH_DELAY_MS);
  });

  if (!getToken() || !readCachedSessionUser()) return;

  const currentOrigin = window.location.origin.toLowerCase();
  let assigned = resolveAssignedApplicationsForSession(user);
  if (assigned.length === 0) {
    assigned = await fetchAllApplications();
  }
  const origins = new Map<string, string>();

  for (const app of assigned) {
    try {
      const origin = new URL(app.url).origin.toLowerCase();
      if (
        origin &&
        origin !== currentOrigin &&
        sameRegistrableDomain(currentOrigin, origin) &&
        !origins.has(origin)
      ) {
        origins.set(origin, app.url);
      }
    } catch {
      /* ignore invalid url */
    }
  }

  if (origins.size === 0) return;

  const pending = new Set(origins.keys());
  const timeout = window.setTimeout(() => cleanup(), 90_000);

  function cleanup(): void {
    window.clearTimeout(timeout);
    window.removeEventListener("message", onMessage);
    document
      .querySelectorAll("iframe[data-variphi-sso-prefetch]")
      .forEach((el) => el.remove());
  }

  function onMessage(event: MessageEvent): void {
    const data = event.data as { type?: string; origin?: string } | null;
    if (!data || data.type !== SSO_PREFETCH_MESSAGE) return;
    const origin = String(data.origin ?? "").toLowerCase();
    pending.delete(origin);
    if (pending.size === 0) cleanup();
  }

  window.addEventListener("message", onMessage);

  for (const appUrl of origins.values()) {
    const iframe = document.createElement("iframe");
    iframe.setAttribute("data-variphi-sso-prefetch", "1");
    iframe.setAttribute("title", "SSO sync");
    iframe.style.cssText = "display:none;width:0;height:0;border:0";
    const base = appUrl.replace(/\/$/, "");
    iframe.src = `${base}/?${SSO_PREFETCH_PARAM}=1`;
    document.body.appendChild(iframe);
  }
}
